import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { allLocationRuns } from "../LocationRun/catalog";
import { buildRunSegments } from "./planSegments";

export type LocationPlacementMode = "cast-column" | "rail-slot";

export interface LocationPlacementSegment {
  startMs: number;
  endMs: number;
  mode: LocationPlacementMode;
  castRole?: "primary" | "secondary";
}

export interface LocationPlacementPlan {
  byRunId: ReadonlyMap<string, readonly LocationPlacementSegment[]>;
}

const planCache = new WeakMap<BookTimelineLayout, LocationPlacementPlan>();

type LayoutForPlacementPlan = Omit<BookTimelineLayout, "locationPlacement">;

/** Whole-chapter cast vs rail slots from stay segments (no playhead heuristics). */
export function buildLocationPlacementPlan(
  layout: LayoutForPlacementPlan,
): LocationPlacementPlan {
  const byRunId = new Map<string, LocationPlacementSegment[]>();
  for (const run of allLocationRuns(layout)) {
    byRunId.set(run.runId, buildRunSegments(layout, run));
  }
  return { byRunId };
}

export function locationPlacementPlan(
  layout: BookTimelineLayout,
): LocationPlacementPlan {
  if (layout.locationPlacement !== undefined) {
    return layout.locationPlacement;
  }
  const cached = planCache.get(layout);
  if (cached !== undefined) {
    return cached;
  }
  const plan = buildLocationPlacementPlan(layout);
  planCache.set(layout, plan);
  return plan;
}

export function locationPlacementAt(
  plan: LocationPlacementPlan,
  runId: string,
  playheadMs: number,
): LocationPlacementSegment | undefined {
  const segments = plan.byRunId.get(runId);
  if (segments === undefined) {
    return undefined;
  }
  for (const segment of segments) {
    if (playheadMs >= segment.startMs && playheadMs < segment.endMs) {
      return segment;
    }
  }
  const last = segments[segments.length - 1];
  if (last !== undefined && playheadMs === last.endMs) {
    return last;
  }
  return undefined;
}

export function runUsesCastColumn(
  layout: BookTimelineLayout,
  runId: string,
  playheadMs: number,
): boolean {
  const segment = locationPlacementAt(
    locationPlacementPlan(layout),
    runId,
    playheadMs,
  );
  return segment?.mode === "cast-column";
}
